import { useState } from "react";
import { FileText, Download, Trash2, UploadCloud, Lightbulb, X, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { SectionSelect, downloadBlob } from "./QuestionParts";
import api from "../../lib/api";
import { toast } from "sonner";
import { t } from "../../lib/i18n";

const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export const DocumentGuide = ({ guide, sections, onPick, onDismiss }) => (
  <div className="hairline bg-[#1E3A5F] text-bone p-6" data-testid="dataroom-guide">
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-start gap-3">
        <Lightbulb className="w-5 h-5 text-[#E4B54B] mt-0.5 flex-shrink-0" />
        <div>
          <span className="font-mono-label text-gold">{t("Guía orientativa")}</span>
          <h3 className="font-serif-display text-xl mt-1">{t("Lo que un comprador suele necesitar para hacer una oferta")}</h3>
          <p className="text-sm text-bone/75 mt-1">{t("Sube lo que quieras y cuando quieras. Estos son los documentos habituales en esta fase.")}</p>
        </div>
      </div>
      <button type="button" onClick={onDismiss} className="text-bone/60 hover:text-bone" aria-label={t("Cerrar guía")} data-testid="dataroom-guide-dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
    <div className="mt-5 grid sm:grid-cols-2 gap-0 border border-r-0 border-b-0" style={{ borderColor: "rgba(255,255,255,0.18)" }}>
      {guide.map((g, i) => (
        <button key={i} type="button" onClick={() => onPick(g.section)}
          className="text-left border border-l-0 border-t-0 p-4 hover:bg-white/5 transition-colors group" style={{ borderColor: "rgba(255,255,255,0.18)" }} data-testid={`dataroom-guide-item-${i + 1}`}>
          <div className="flex items-center justify-between gap-3">
            <span className="font-medium text-sm">{g.title}</span>
            <ArrowRight className="w-3 h-3 text-gold opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <p className="text-xs text-bone/65 mt-1 leading-relaxed">{g.hint}</p>
          <span className="font-mono-label text-gold mt-2 inline-block" style={{ fontSize: "0.58rem" }}>{t("Sección ·")}{" "}{sections[g.section]}</span>
        </button>
      ))}
    </div>
  </div>
);

export const UploadBox = ({ companyId, sections, section, setSection, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [inputKey, setInputKey] = useState(0);

  const upload = async () => {
    if (!file) return toast.error(t("Selecciona un archivo"));
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("section", section);
      fd.append("file", file);
      await api.post(`/companies/${companyId}/dataroom/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("Documento publicado en el Data Room"));
      setFile(null);
      setInputKey((k) => k + 1);
      onUploaded();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error subiendo el documento");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hairline bg-card p-5" data-testid="dataroom-upload-box">
      <span className="font-mono-label text-[#E0712C]">{t("Subir documento")}</span>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <SectionSelect sections={sections} value={section} onChange={setSection} testId="dataroom-upload-section" />
        <Input key={inputKey} type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="rounded-none h-9 flex-1 min-w-[220px] text-xs" data-testid="dataroom-upload-file" />
        <Button onClick={upload} disabled={busy || !file} className="rounded-none h-9 px-5 bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone" data-testid="dataroom-upload-btn">
          <UploadCloud className="w-3 h-3 mr-2" /> {busy ? t("Subiendo…") : t("Publicar")}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-2">{t("PDF, Excel, Word, PowerPoint, imágenes o ZIP · máx. 20 MB · visible para todos los compradores con NDA aprobado.")}</p>
    </div>
  );
};

export const DocumentRow = ({ d, isOwner, onDeleted }) => {
  const remove = async () => {
    if (!window.confirm(`¿Eliminar "${d.filename}" del Data Room?`)) return;
    try {
      await api.delete(`/dataroom/documents/${d.document_id}`);
      toast.success(t("Documento eliminado"));
      onDeleted();
    } catch {
      toast.error(t("No se pudo eliminar"));
    }
  };
  return (
    <div className="px-6 py-4 flex items-center gap-4" data-testid={`dr-document-${d.document_id}`}>
      <div className="w-9 h-9 hairline bg-bone flex items-center justify-center flex-shrink-0">
        <FileText className="w-4 h-4 text-[#1E3A5F]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{d.filename}</p>
        <p className="font-mono-label text-muted-foreground" style={{ fontSize: "0.58rem" }}>
          {fmtSize(d.size || 0)} · {new Date(d.uploaded_at).toLocaleDateString("es-ES")}
        </p>
      </div>
      <button type="button" onClick={() => downloadBlob(`/dataroom/documents/${d.document_id}/download`, d.filename)}
        className="font-mono-label text-[#1E3A5F] inline-flex items-center gap-1 hover:opacity-70" data-testid={`dr-download-${d.document_id}`}>
        <Download className="w-3 h-3" />{" "}{t("Descargar")}
      </button>
      {isOwner && (
        <button type="button" onClick={remove} className="text-muted-foreground hover:text-destructive" aria-label={t("Eliminar")} data-testid={`dr-delete-${d.document_id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};
